import {
  Contact,
  EnumOrderSourceType,
  EnumOwnerType,
  EnumProductType,
  EnumScheduledPaymentStatus,
  EnumScheduledPaymentType,
  OrderByIdQuery,
  RefundManyQuery,
  ScheduledPaymentFindOneQuery,
  useBookerByIdQuery,
  useEventByIdQuery,
  useOperatorByIdQuery,
  useOrderByIdQuery,
  useOrderPricesByIdQuery,
  useRefundManyQuery,
  useRenewablePurchaseByIdQuery,
  useRenewablePurchaseByIdPublicQuery,
  useScheduledPaymentFindOneQuery,
} from "gql/generated";
import { useMemo } from "react";
import { getProductTypeConfig } from "shared/config/productTypeConfig";
import { getMultilingualString, mergeObjectsWithFallback, resolveEventGettingHereAddress } from "shared/utils";

export type UseReservationDetailsProps = {
  orderId: string;
  isPublic?: boolean;
  preloadedOrder?: OrderByIdQuery["orderById"];
  preloadedRefunds?: RefundManyQuery["refundMany"];
  preloadedFailedScheduledPayment?: ScheduledPaymentFindOneQuery["scheduledPaymentFindOne"];
};

export const useReservationDetails = ({
  orderId,
  isPublic,
  preloadedOrder,
  preloadedRefunds,
  preloadedFailedScheduledPayment,
}: UseReservationDetailsProps) => {
  // Load the order data
  const { data, loading, error } = useOrderByIdQuery({
    variables: {
      id: orderId,
    },
    skip: !!preloadedOrder,
  });
  const order = preloadedOrder ?? data?.orderById;

  // Load the refunds data
  const {
    data: refundsData,
    loading: refundsLoading,
    error: refundsError,
  } = useRefundManyQuery({
    variables: {
      filter: {
        orderId,
      },
    },
    skip: !!preloadedRefunds,
  });
  const refunds = preloadedRefunds ?? refundsData?.refundMany;

  // Load the booker data
  const {
    data: bookerData,
    loading: bookerLoading,
    error: bookerError,
  } = useBookerByIdQuery({
    variables: { id: order?.source.booker },
    skip: !order?.source.booker,
  });

  // Load the owner (Operator) data. Products can also be owned by a
  // storefront or organization (e.g. the blank-invoice placeholder product);
  // `operatorById` is non-nullable and errors the whole page for those, so
  // only look up genuine operator owners.
  const mainProductOwner = order?.purchasedProducts[0]?.product.owner;
  const freeTextPurchaserBusiness = order?.source?.businessName?.trim() || "";
  const checkoutOperatorId =
    !freeTextPurchaserBusiness &&
    order?.source?.operatorId &&
    order.source.operatorId !== mainProductOwner?.entityId
      ? order.source.operatorId
      : undefined;
  const {
    data: operatorData,
    loading: operatorLoading,
    error: operatorError,
  } = useOperatorByIdQuery({
    variables: {
      operatorId: mainProductOwner?.entityId,
    },
    skip:
      !order?.purchasedProducts[0]?.product?.contact?.useOwnerContact ||
      mainProductOwner?.type !== EnumOwnerType.Operator,
  });
  const {
    data: checkoutOperatorData,
    loading: checkoutOperatorLoading,
  } = useOperatorByIdQuery({
    variables: {
      operatorId: checkoutOperatorId ?? "",
    },
    skip: !checkoutOperatorId,
  });

  // Get the main product, its type and the support email
  const { mainProduct, productTypeConfig, contactInfo } = useMemo(() => {
    const mainProduct = order?.purchasedProducts?.[0]?.product;
    return {
      mainProduct,
      productTypeConfig: getProductTypeConfig(mainProduct?.type),
      contactInfo: mainProduct?.contact?.useOwnerContact
        ? operatorData?.operatorById?.contact
        : // We need to fallback to the operator's contact info if any of the product's contact info is blank
          mergeObjectsWithFallback<Contact>(
            (mainProduct?.contact as Contact) ?? {},
            operatorData?.operatorById?.contact ?? {},
          ),
    };
  }, [order?.purchasedProducts, operatorData?.operatorById]);

  const eventId =
    mainProduct?.type === EnumProductType.Event
      ? mainProduct.eventMetadata?.event
      : undefined;

  const { data: eventData, loading: eventLoading } = useEventByIdQuery({
    variables: { id: eventId ?? "" },
    skip: !eventId,
  });

  const gettingHereAddress = useMemo(() => {
    if (mainProduct?.type === EnumProductType.Event) {
      const venueAddress = resolveEventGettingHereAddress(eventData?.eventById);
      if (venueAddress) return venueAddress;
    }
    return contactInfo?.address;
  }, [contactInfo?.address, eventData?.eventById, mainProduct?.type]);

  // Get the order prices
  const {
    data: orderPricesData,
    loading: orderPricesLoading,
    error: orderPricesError,
  } = useOrderPricesByIdQuery({
    variables: {
      orderId,
    },
  });

  // Failed scheduled payments
  const {
    data: failedScheduledPaymentsData,
    loading: failedScheduledPaymentsLoading,
    error: failedScheduledPaymentsError,
  } = useScheduledPaymentFindOneQuery({
    variables: {
      filter: {
        order: orderId,
        status: EnumScheduledPaymentStatus.Failed,
        type: EnumScheduledPaymentType.RemainingBalance,
      },
    },
  });

  const failedScheduledPayment =
    preloadedFailedScheduledPayment ??
    failedScheduledPaymentsData?.scheduledPaymentFindOne;

  const isRenewalOrder =
    order?.source?.type === EnumOrderSourceType.Renewal &&
    !!order.source.renewablePurchaseId;

  const {
    data: renewablePurchasePrivateData,
    loading: renewablePurchasePrivateLoading,
  } = useRenewablePurchaseByIdQuery({
    variables: { id: order?.source?.renewablePurchaseId ?? "" },
    skip: !isRenewalOrder || !!isPublic,
  });

  const {
    data: renewablePurchasePublicData,
    loading: renewablePurchasePublicLoading,
  } = useRenewablePurchaseByIdPublicQuery({
    variables: { id: order?.source?.renewablePurchaseId ?? "" },
    skip: !isRenewalOrder || !isPublic,
  });

  const renewablePurchaseData = isPublic
    ? renewablePurchasePublicData?.renewablePurchaseByIdPublic
    : renewablePurchasePrivateData?.renewablePurchaseById;
  const renewablePurchaseLoading = isPublic
    ? renewablePurchasePublicLoading
    : renewablePurchasePrivateLoading;

  const purchaserBusinessName =
    freeTextPurchaserBusiness ||
    (checkoutOperatorData?.operatorById?.name
      ? getMultilingualString(checkoutOperatorData.operatorById.name)
      : "");

  return {
    order,
    booker: bookerData?.bookerById,
    orderPrices: orderPricesData?.orderPricesById,
    owner: operatorData?.operatorById,
    refunds,
    failedScheduledPayment,
    renewablePurchase: renewablePurchaseData ?? null,
    loading:
      loading ||
      operatorLoading ||
      bookerLoading ||
      orderPricesLoading ||
      refundsLoading ||
      failedScheduledPaymentsLoading ||
      renewablePurchaseLoading ||
      checkoutOperatorLoading ||
      eventLoading,
    error:
      error ||
      operatorError ||
      bookerError ||
      orderPricesError ||
      refundsError ||
      failedScheduledPaymentsError,
    productTypeConfig,
    mainProduct,
    contactInfo,
    gettingHereAddress,
    purchaserBusinessName,
  };
};
